// app/shared/components/workflow-renderer/BaseCardComponent.tsx
import { LucideIcon } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

interface ComponentStyle {
  colorName: string;
  accentBgClass: string;
  iconContainerBgClass: string;
  iconTextColorClass: string;
  iconBorderClass: string;
}

export const COMPONENT_TYPE_STYLES: Record<string, ComponentStyle> = {
    AIAgent:    { colorName: 'purple', accentBgClass: 'bg-purple-500', iconContainerBgClass: 'bg-purple-100', iconTextColorClass: 'text-purple-600', iconBorderClass: 'border-purple-200' },
    DataStore:  { colorName: 'teal',   accentBgClass: 'bg-teal-500',   iconContainerBgClass: 'bg-teal-100',   iconTextColorClass: 'text-teal-600',   iconBorderClass: 'border-teal-200' },
    VectorDB:   { colorName: 'sky',    accentBgClass: 'bg-sky-500',    iconContainerBgClass: 'bg-sky-100',    iconTextColorClass: 'text-sky-600',    iconBorderClass: 'border-sky-200' },
    Database:   { colorName: 'teal',   accentBgClass: 'bg-teal-500',   iconContainerBgClass: 'bg-teal-100',   iconTextColorClass: 'text-teal-600',   iconBorderClass: 'border-teal-200' },
    Trigger:    { colorName: 'orange', accentBgClass: 'bg-orange-500', iconContainerBgClass: 'bg-orange-100', iconTextColorClass: 'text-orange-600', iconBorderClass: 'border-orange-200' },
    Tool:       { colorName: 'brown',  accentBgClass: 'bg-brown',  iconContainerBgClass: 'bg-brown',  iconTextColorClass: 'text-white',  iconBorderClass: 'border-white' },
    DecisionNode:{colorName: 'yellow', accentBgClass: 'bg-yellow-500', iconContainerBgClass: 'bg-yellow-100', iconTextColorClass: 'text-yellow-700', iconBorderClass: 'border-yellow-300' },
    HumanTask:  { colorName: 'teal',   accentBgClass: 'bg-teal-500',   iconContainerBgClass: 'bg-teal-100',   iconTextColorClass: 'text-teal-600',   iconBorderClass: 'border-teal-200' },
    DataSource: { colorName: 'lime',   accentBgClass: 'bg-lime-500',   iconContainerBgClass: 'bg-lime-100',   iconTextColorClass: 'text-lime-700',   iconBorderClass: 'border-lime-300' },
    DataTransformation: { colorName: 'indigo', accentBgClass: 'bg-indigo-500', iconContainerBgClass: 'bg-indigo-100', iconTextColorClass: 'text-indigo-600', iconBorderClass: 'border-indigo-200' },
    Notifier:   { colorName: 'pink',   accentBgClass: 'bg-pink-500',   iconContainerBgClass: 'bg-pink-100',   iconTextColorClass: 'text-pink-600',   iconBorderClass: 'border-pink-200' },
    Conclusion: { colorName: 'rose',   accentBgClass: 'bg-rose-500',   iconContainerBgClass: 'bg-rose-100',   iconTextColorClass: 'text-rose-600',   iconBorderClass: 'border-rose-200' },
    default:    { colorName: 'gray',   accentBgClass: 'bg-gray-500',   iconContainerBgClass: 'bg-gray-100',   iconTextColorClass: 'text-gray-600',   iconBorderClass: 'border-gray-200' }
};


export interface BaseCardComponentProps {
  title: string;
  description: string;
  componentType: string;
  icon: LucideIcon;
  badgeText?: string;
  className?: string;
  children: React.ReactNode;
  badgeClassName?: string;
}

const getContrastTextColor = (bgColorName: string): string => {
    switch (bgColorName) {
        case 'yellow':
        case 'lime':
        case 'amber':
            return 'text-black';
        case 'gray':
             return 'text-white';
        default:
            return 'text-white';
    }
};

export function BaseCardComponent({
    title,
    description,
    componentType,
    icon: IconComponent,
    badgeText,
    className,
    children,
    badgeClassName
  }: BaseCardComponentProps) {
    const style = COMPONENT_TYPE_STYLES[componentType] || COMPONENT_TYPE_STYLES.default;
    const badgeTextColorClass = getContrastTextColor(style.colorName);

    return (
        <div className={cn(
            "bg-[#F0EBE6] rounded-lg border border-gray-300 shadow-lg overflow-hidden flex flex-col mb-4",
            className
          )}>
        <div className={cn("h-1.5", style.accentBgClass)} />

        <div className="p-2 md:p-4 flex-grow flex flex-col">
          {/* Mobile layout: Icon and badge on first row */}
          <div className="flex md:hidden items-center justify-between mb-3">
            <div className={cn(
              `w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border`,
              style.iconContainerBgClass,
              style.iconBorderClass
            )}>
              <IconComponent size={20} className={style.iconTextColorClass} />
            </div>
            {badgeText && (
              <span className={cn(
                "text-xs font-medium py-1 px-2.5 rounded-md whitespace-nowrap",
                style.accentBgClass,
                badgeTextColorClass,
                badgeClassName
              )}>
                {badgeText}
              </span>
            )}
          </div>

          {/* Mobile layout: Title and description full width */}
          <div className="md:hidden mb-3">
            <h4 className="text-md font-semibold text-gray-900 mb-1">{title}</h4>
            <p className="text-sm text-gray-700">{description}</p>
          </div>

          {/* Desktop layout: Icon, title, description, and badge in one row */}
          <div className="hidden md:flex items-start mb-3">
            <div className={cn(
              `w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 border`,
              style.iconContainerBgClass,
              style.iconBorderClass
            )}>
              <IconComponent size={20} className={style.iconTextColorClass} />
            </div>
            <div className="flex-grow">
              <h4 className="text-md font-semibold text-gray-900">{title}</h4>
              <p className="text-sm text-gray-700 mt-0.5">{description}</p>
            </div>
            {badgeText && (
              <span className={cn(
                "ml-2 text-xs font-medium py-1 px-2.5 rounded-md whitespace-nowrap self-start",
                style.accentBgClass,
                badgeTextColorClass,
                badgeClassName
              )}>
                {badgeText}
              </span>
            )}
          </div>

          <div className="flex-grow mt-1">
            {children}
          </div>
        </div>
      </div>
    );
  }
